import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  ContractStatus,
  CustomerService,
  DocumentSortField,
  FileExtension,
  getContracts,
  getContractsCount,
  getDocumentSorting,
  getResidentAppSettings,
  getSelectedContract,
  getUnread,
  GetSharedContent,
  getSharedContent,
  getSharedContentActionState,
  IContract,
  ImmomioIconName,
  ISharedDocumentsResponse,
  ISort,
  LoadingType,
  LoadMetersByContract,
  SelectContract,
  SortType,
  WhitelabelImage,
  WhitelabelImageAltTag,
} from '@resident-nx/shared';
import { combineLatest, map, pairwise, startWith, tap } from 'rxjs';
import { FileDownloadWebService } from '../../../services';
import {
  ElevationDirective,
  FilePreviewModalComponent,
  FilePreviewModalData,
  FilePreviewModalFile,
  IconWebComponent,
  WhitelabelImageWebComponent,
  ContractPickerComponent,
} from '@resident-nx/web/ui';
import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { LoadingSkeletonWebComponent } from '../../atoms/loading-skeleton/loading-skeleton.component';
import { SortingMenuComponent } from '../sorting-menu/sorting-menu.component';
import { TranslatePipe } from '@ngx-translate/core';
import { A11yPressDirective } from '../../../directives/a11y/a11y-press.directive';
import { DocumentHintModalWebComponent } from '../../molecules/document-hint-modal/document-hint-modal.component';
import { FeedbackStore } from '@resident-nx/web/feedback/data-access';
import { FeedbackModalComponent } from '@resident-nx/web/feedback';

@Component({
  standalone: true,
  selector: 'rs-web-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ContractPickerComponent,
    SortingMenuComponent,
    IconWebComponent,
    LoadingSkeletonWebComponent,
    AsyncPipe,
    TranslatePipe,
    WhitelabelImageWebComponent,
    ElevationDirective,
    A11yPressDirective,
    DatePipe,
    NgTemplateOutlet,
  ],
})
export class DocumentsWebComponent implements OnInit, OnDestroy {
  #customerService = inject(CustomerService);
  #destroyRef = inject(DestroyRef);
  #store = inject(Store);
  #downloadService = inject(FileDownloadWebService);
  #dialog = inject(Dialog);
  #feedbackStore = inject(FeedbackStore);
  #ngbModal = inject(NgbModal);

  // Types
  public immomioIconName = ImmomioIconName;
  public sortTypes = SortType;
  protected readonly LoadingType = LoadingType;
  protected readonly ContractStatus = ContractStatus;
  public whitelabelImage = WhitelabelImage;
  public WhitelabelImageAltTag = WhitelabelImageAltTag;

  // query params to open documents automatically with external url
  public documentId = input<string>();
  public documentName = input<string>();
  public documentExtension = input<FileExtension>();

  public folderId = input<string>();
  public isChild = input(false);
  public selectedFolderId = linkedSignal(this.folderId);
  public documentSortSupported = toSignal(
    this.#store
      .select(getResidentAppSettings)
      .pipe(map(settings => settings?.documentSortingSupported))
  );
  public numberOfContracts$ = this.#store.select(getContractsCount);
  #contracts = this.#store.selectSignal(getContracts);
  #selectedContract = this.#store.selectSignal(getSelectedContract);
  protected readonly contractOptions = computed(() =>
    this.#contracts().map(contract => ({
      ...contract,
      disabled: contract.status === ContractStatus.EXPIRED,
    }))
  );
  protected readonly selectedContractId = computed(() => this.#selectedContract()?.id);
  protected readonly hasSingleContract = computed(() => this.contractOptions().length === 1);
  #unread = this.#store.selectSignal(getUnread);
  protected readonly unreadContractIds = computed(() => {
    const unread = this.#unread()?.unreadDocuments;
    // Contractless unread documents concern every contract.
    if (unread?.contractless) {
      return this.contractOptions().map(contract => contract.id);
    }
    return unread?.contractIds ?? [];
  });

  public previewOpen = signal(false);
  public isContentLoading = toSignal(
    this.#store.select(getSharedContentActionState).pipe(map(state => state.pending))
  );
  public sharedContent = this.#store.selectSignal(getSharedContent);
  private selectedContract = signal<IContract | undefined>(undefined);
  private documentSorting = signal<ISort<DocumentSortField> | undefined>(undefined);

  public onlyExpiredContractsAndActiveMembership$ =
    this.#customerService.onlyExpiredContractsAndActiveMembership();

  constructor() {
    effect(() => {
      const documentId = this.documentId();

      if (documentId) {
        untracked(() => {
          const document: Omit<ISharedDocumentsResponse, 'sharedWithMeAt' | 'extension'> = {
            id: documentId,
            name: this.documentName() || '',
            unread: false,
          };
          this.preview(document);
        });
      }
    });

    effect(() => {
      const data = this.sharedContent();

      if (data?.shouldSeeMigrationModal) {
        untracked(() => {
          this.#ngbModal.open(DocumentHintModalWebComponent, { centered: true });
        });
      }
    });
  }

  ngOnInit() {
    combineLatest([
      this.#store.select(getSelectedContract).pipe(
        startWith(undefined),
        pairwise(),
        tap(([prevContract, currContract]) => {
          if (prevContract?.id !== currContract?.id) {
            this.selectedFolderId.set(undefined);
          }
        }),
        map(([_, curr]) => curr)
      ),
      this.#store.select(getDocumentSorting),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([contract, sorting]) => {
        this.documentSorting.set(sorting);
        this.selectedContract.set(contract);
        this.fetchSharedContent();
      });
  }

  ngOnDestroy(): void {
    this.#dialog.closeAll();
  }

  public selectContract(id: string): void {
    if (!id || id === this.selectedContractId()) {
      return;
    }
    this.#store.dispatch(SelectContract({ id }));
    this.#store.dispatch(LoadMetersByContract());
  }

  public preview(document: Omit<ISharedDocumentsResponse, 'sharedWithMeAt' | 'extension'>) {
    const file: FilePreviewModalFile = {
      identifier: document.id,
      name: document.name,
    };

    const data: FilePreviewModalData = {
      files: [file],
    };

    const dialogRef = this.#dialog.open(FilePreviewModalComponent, {
      data,
    });

    const modalComponent = dialogRef.componentInstance as FilePreviewModalComponent;

    modalComponent.download.subscribe(fileToDownload =>
      this.#downloadService.download(fileToDownload.downloadUrl || '', fileToDownload.name)
    );

    dialogRef.closed.subscribe(() => {
      if (modalComponent.previewWasSeen()) {
        this.#feedbackStore.promptIfEligible(FeedbackModalComponent);
      }
    });

    this.#downloadService
      .getDocument(document.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(docData => {
        const updatedFile: FilePreviewModalFile = {
          ...file,
          fileUrl: docData.preview,
          downloadUrl: docData.download,
          extension: docData.extension,
          isRequested: docData.isRequested,
          isAlreadyRequested: docData.isAlreadyRequested,
          isUnavailable: docData.isUnavailable,
        };
        modalComponent.files.set([updatedFile]);
      });
  }

  public openFolder(folderId: string): void {
    this.selectedFolderId.set(folderId);
    this.fetchSharedContent();
  }

  private fetchSharedContent() {
    this.#store.dispatch(
      GetSharedContent({
        params: {
          folderId: this.selectedFolderId(),
          contractId: this.selectedContract()?.id,
          sortField: this.documentSorting()?.value,
          sortOrder: this.documentSorting()?.direction,
        },
      })
    );
  }
}
